import type {
  AssetManifest,
  InventoryResponse,
  MarketBrowseResponse,
  MarketCategory,
  MarketHistoryResponse,
  MarketListingWire,
  MarketQuoteWire,
  MarketSort,
  Rarity,
} from '@kenocyber/shared';
import {
  countByMarketCategory,
  isInMarketCategory,
  itemDisplayName,
  itemLevelLabel,
  itemSearchKey,
  MARKET_CATEGORIES,
  MARKET_CATEGORY_LABELS,
  MARKET_FEE_NON_VIP_PERCENT,
  MARKET_FEE_TIMING_NOTICE,
  MARKET_FEE_VIP_PERCENT,
  MISSING_ART_LABEL,
  marketCategoryOf,
  TRADE_BLOCK_MESSAGES,
} from '@kenocyber/shared';
import { useCallback, useEffect, useState } from 'react';
import {
  browseMarket,
  buyMarketListing,
  cancelMarketListing,
  createMarketListing,
  fetchInventory,
  fetchMarketHistory,
  fetchMarketQuote,
  fetchMyListings,
  HuntApiError,
} from '../api/hunt';
import { itemArtUrl } from '../game/assets';
import { formatDecimal } from './format';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * The item marketplace, as a floating panel over the arena.
 *
 * ## It never touches the hunt
 *
 * `position: absolute` like every other panel, so opening it does not resize or
 * recreate the Phaser canvas. It sends no bridge message and holds no
 * simulation state. Buying an artifact does change what the hunt is worth - but
 * the SERVER reconfigures the running session in place, and this panel learns
 * about it the same way it learns everything else: by asking.
 *
 * ## No polling of the public list
 *
 * A browse is a page of somebody else's listings, and re-fetching all of it
 * every three seconds would be a request nobody asked for. It refreshes when
 * the player asks, and it is invalidated after an action that changed something
 * - listing, cancelling or buying.
 *
 * ## Nothing is predicted
 *
 * A sale moves real items and real currency, so every number here came from the
 * server's last answer.
 */

type TabKey = 'buy' | 'sell' | 'mine' | 'history';

const TABS: readonly { key: TabKey; label: string }[] = [
  { key: 'buy', label: 'COMPRAR' },
  { key: 'sell', label: 'VENDER' },
  { key: 'mine', label: 'MEUS ANÚNCIOS' },
  { key: 'history', label: 'HISTÓRICO' },
];

const SORTS: readonly { key: MarketSort; label: string }[] = [
  { key: 'recent', label: 'Mais recentes' },
  { key: 'price_asc', label: 'Menor preço' },
  { key: 'price_desc', label: 'Maior preço' },
];

const STATE_LABELS: Record<string, string> = {
  active: 'ATIVO',
  sold: 'VENDIDO',
  cancelled: 'CANCELADO',
  expired: 'EXPIRADO',
};

interface MarketPanelProps {
  readonly manifest: AssetManifest;
  readonly onClose: () => void;
  /** Told when a sale or purchase settles, so the vault can be re-read. */
  readonly onSettled: () => void;
}

/** A price field that only ever holds digits. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '') || '0';
}

/** What one history row actually was, from the caller's side. */
function outcomeLabel(entry: { outcome: string; role: string }): string {
  if (entry.outcome === 'cancelled') {
    return 'CANCELADO';
  }
  if (entry.outcome === 'expired') {
    return 'EXPIRADO';
  }
  return entry.role === 'seller' ? 'VENDA' : 'COMPRA';
}

/**
 * An ESTIMATE of what a listing would cost at a given tier.
 *
 * Deliberately not the authority: the real fee is computed on the server at the
 * moment of sale, with the seller's carry, and can differ by one unit - or by
 * the whole tier if VIP lapses first. This exists so "meus anúncios" can show a
 * number without a request per row.
 */
function estimateFee(price: string, tier: 'vip' | 'standard'): string {
  const value = BigInt(price);
  const [numerator, denominator] = tier === 'vip' ? [4n, 25n] : [23n, 100n];
  return ((value * numerator) / denominator).toString(10);
}

function estimateNet(price: string, tier: 'vip' | 'standard'): string {
  return (BigInt(price) - BigInt(estimateFee(price, tier))).toString(10);
}

/**
 * Says once, in DEVELOPMENT only, that a definition has no picture.
 *
 * `import.meta.env.DEV` is a literal the bundler folds away, so this whole
 * function - and the `Set` behind it - is dead code in a production build. It
 * is a note for whoever is adding content, never something a player can reach:
 * what THEY see is a framed placeholder with a sentence in it.
 *
 * Once per definition, because a grid of forty cards should produce one line.
 */
const warnedArt = new Set<string>();

function warnMissingArt(definitionId: string): void {
  if (!import.meta.env.DEV || warnedArt.has(definitionId)) {
    return;
  }
  warnedArt.add(definitionId);
  console.warn(
    `[market] no art for "${definitionId}" - showing "${MISSING_ART_LABEL}". ` +
      `None of the four manifests describes it: add it to the asset pipeline.`,
  );
}

/**
 * The picture of one item, wherever it appears.
 *
 * ONE component for all four tabs. The buy grid had art and the other three
 * were lists of text, which made the same pet look like two different kinds of
 * thing depending on which tab you were on.
 *
 * A arte sai de `itemArtUrl`, que é o passo do cliente para o resolvedor
 * canónico `itemArt` — o mesmo que a carga de campo, as caixas, a célula do
 * inventário e o painel de detalhe usam. Nada aqui monta um caminho nem sabe o
 * que é um pet.
 *
 * **Era `item.assetKey`, e essa é a chave do manifesto GERAL** (gate
 * V28I.BI.2). Ela é nula para uma joia, uma carta e uma skin, cuja arte vive num
 * manifesto próprio — e uma joia negociada, que passou a ser negociável no
 * V28I.BI, aparecia como "Imagem indisponível" com a imagem publicada ao lado.
 *
 * O nível entra porque a arte de um pet é a da EVOLUÇÃO dele, e não do pet: sem
 * ele um EV. III mostraria a miniatura do EV. I. Ele já viajava nas duas formas
 * que alimentam este componente.
 *
 * A miniatura de um pet é um PNG que o pipeline já gerou; não há Three.js neste
 * ecrã e não pode haver, em tamanho nenhum. Quarenta cartas seriam quarenta
 * contextos WebGL.
 *
 * `loading="lazy"` and `decoding="async"` so a long history does not decode
 * every row before drawing the first, and the browser's own cache means the
 * same picture on twenty rows is one request.
 */
function ItemThumb({
  manifest,
  item,
  size,
}: {
  manifest: AssetManifest;
  item: { definitionId: string; rarity: Rarity; level: number };
  /**
   * `card` fills a grid tile, `row` is the small square in a list, `preview` is
   * the larger one beside the sell form. Three sizes, one component: the
   * preview must be the SAME picture the row showed, resolved the same way.
   */
  size: 'card' | 'row' | 'preview';
}) {
  const url = itemArtUrl(manifest, item.definitionId, item.level);
  if (!url) {
    warnMissingArt(item.definitionId);
  }
  return (
    <div
      className={
        size === 'card'
          ? 'market-card-art'
          : size === 'preview'
            ? 'market-thumb is-preview'
            : 'market-thumb'
      }
      data-rarity={item.rarity}
      data-paint={rarityPaint(item.rarity)}
      style={rarityStyle(item.rarity)}
    >
      {url ? (
        <img src={url} alt="" draggable={false} loading="lazy" decoding="async" />
      ) : (
        // A framed KENOCYBER placeholder, never a bare `?` and never the
        // definition id: a player looking at something they may spend Gold on
        // should read a sentence, not an internal identifier.
        <span
          className="market-card-noart"
          data-testid="market-card-noart"
          title={MISSING_ART_LABEL}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <title>Sem arte</title>
            <path
              d="M3 5h18v14H3z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              opacity="0.55"
            />
            <path d="M3 16l5-5 4 4 3-3 6 6" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="8.5" cy="8.5" r="1.4" fill="currentColor" />
          </svg>
          <span className="market-noart-label">{MISSING_ART_LABEL}</span>
        </span>
      )}
    </div>
  );
}

/**
 * The eight drawers, as a compact strip above whichever list is open.
 *
 * `flex-wrap`, so at 1366 they fall onto a second line in order rather than
 * scrolling sideways or clipping the last one.
 *
 * `counts` is optional because only the sell tab honestly has them: the vault
 * is fully loaded there, while the public browse is paginated and any number
 * this screen invented for it would be a guess about somebody else's page. An
 * empty drawer is disabled but still drawn - a category that vanishes when you
 * own none of it makes the strip move under the cursor.
 */
function CategoryTabs({
  value,
  counts,
  disabled,
  onChange,
}: {
  value: MarketCategory;
  counts?: Readonly<Record<MarketCategory, number>>;
  disabled: boolean;
  onChange: (category: MarketCategory) => void;
}) {
  return (
    <div className="market-categories" role="tablist" aria-label="Categorias">
      {MARKET_CATEGORIES.map((key) => {
        const count = counts?.[key];
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={value === key}
            className={value === key ? 'is-active' : ''}
            data-testid={`market-category-${key}`}
            disabled={disabled || count === 0}
            onClick={() => onChange(key)}
          >
            {MARKET_CATEGORY_LABELS[key]}
            {count === undefined ? '' : ` (${count})`}
          </button>
        );
      })}
    </div>
  );
}

/** Whole hours left, or `—` once terminal. */
function remainingLabel(expiresAt: string | null): string {
  if (!expiresAt) {
    return '—';
  }
  const ms = Date.parse(expiresAt) - Date.now();
  if (ms <= 0) {
    return 'expirando';
  }
  const days = Math.floor(ms / 86_400_000);
  return days >= 1 ? `${days} d` : `${Math.max(1, Math.floor(ms / 3_600_000))} h`;
}

export function MarketPanel({ manifest, onClose, onSettled }: MarketPanelProps) {
  const [tab, setTab] = useState<TabKey>('buy');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [browse, setBrowse] = useState<MarketBrowseResponse | null>(null);
  const [mine, setMine] = useState<MarketBrowseResponse | null>(null);
  const [history, setHistory] = useState<MarketHistoryResponse | null>(null);
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);

  const [sort, setSort] = useState<MarketSort>('recent');
  const [nameFilter, setNameFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState<'' | 'gold' | 'cash'>('');
  /**
   * The open drawer, shared by all four tabs.
   *
   * One value rather than one per tab: a player who came here looking for a pet
   * is still looking for a pet when they check what they have listed. Changing
   * it re-reads the current list from the start, which resets only the CURSOR -
   * every refresh below is called without one.
   */
  const [category, setCategory] = useState<MarketCategory>('all');
  /** The sell tab's own search, applied inside the open drawer. */
  const [sellSearch, setSellSearch] = useState('');

  /** The sell tab's draft: an item, a currency and a price. */
  const [sellItem, setSellItem] = useState<string | null>(null);
  const [sellCurrency, setSellCurrency] = useState<'gold' | 'cash'>('gold');
  const [sellPrice, setSellPrice] = useState('1000');
  const [quote, setQuote] = useState<MarketQuoteWire | null>(null);

  /** The listing awaiting a confirmation click. */
  const [confirming, setConfirming] = useState<MarketListingWire | null>(null);

  // Stable identity, so the refresh callbacks below can depend on it honestly
  // rather than closing over a function React recreates every render.
  const report = useCallback((unknownError: unknown) => {
    setError(
      unknownError instanceof HuntApiError
        ? (unknownError.reason ?? 'Falha na operação')
        : 'Falha na operação',
    );
  }, []);

  const refreshBrowse = useCallback(async () => {
    try {
      setBrowse(
        await browseMarket({
          sort,
          ...(category === 'all' ? {} : { category }),
          ...(nameFilter ? { name: nameFilter } : {}),
          ...(currencyFilter ? { currency: currencyFilter } : {}),
        }),
      );
    } catch (unknownError) {
      report(unknownError);
    }
  }, [sort, category, nameFilter, currencyFilter, report]);

  const refreshMine = useCallback(async () => {
    try {
      setMine(await fetchMyListings({ category }));
    } catch (unknownError) {
      report(unknownError);
    }
  }, [category, report]);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await fetchMarketHistory({ category }));
    } catch (unknownError) {
      report(unknownError);
    }
  }, [category, report]);

  /**
   * Appends the next page, keeping what is already on screen.
   *
   * Keyset, so the cursor is the last row's own position: no offset to drift,
   * and no row seen twice or skipped when somebody lists something mid-browse.
   */
  const loadMore = useCallback(
    async (which: 'buy' | 'mine' | 'history') => {
      setBusy(true);
      try {
        if (which === 'buy' && browse?.nextCursor) {
          const next = await browseMarket({
            sort,
            cursor: browse.nextCursor,
            ...(category === 'all' ? {} : { category }),
            ...(nameFilter ? { name: nameFilter } : {}),
            ...(currencyFilter ? { currency: currencyFilter } : {}),
          });
          setBrowse({ ...next, listings: [...browse.listings, ...next.listings] });
        } else if (which === 'mine' && mine?.nextCursor) {
          const next = await fetchMyListings({ cursor: mine.nextCursor, category });
          setMine({ ...next, listings: [...mine.listings, ...next.listings] });
        } else if (which === 'history' && history?.nextCursor) {
          const next = await fetchMarketHistory({ cursor: history.nextCursor, category });
          setHistory({ ...next, entries: [...history.entries, ...next.entries] });
        }
      } catch (unknownError) {
        report(unknownError);
      } finally {
        setBusy(false);
      }
    },
    [browse, mine, history, sort, category, nameFilter, currencyFilter, report],
  );

  const refreshInventory = useCallback(async () => {
    try {
      setInventory(await fetchInventory());
    } catch {
      // A failed read leaves the previous list on screen; the tab says so.
    }
  }, []);

  useEffect(() => {
    if (tab === 'buy') {
      void refreshBrowse();
    } else if (tab === 'mine') {
      void refreshMine();
    } else if (tab === 'history') {
      void refreshHistory();
    } else {
      void refreshInventory();
    }
  }, [tab, refreshBrowse, refreshMine, refreshHistory, refreshInventory]);

  /** The quote follows the draft, and says which rate it used. */
  useEffect(() => {
    if ((tab !== 'sell' && tab !== 'mine') || sellPrice === '0') {
      return;
    }
    let cancelled = false;
    void fetchMarketQuote(sellPrice, sellCurrency)
      .then((response) => {
        if (!cancelled) {
          setQuote(response.quote);
        }
      })
      .catch(() => {
        /* the tab still shows the constants below */
      });
    return () => {
      cancelled = true;
    };
  }, [tab, sellPrice, sellCurrency]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  /** Runs an action and invalidates everything it could have changed. */
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await Promise.all([refreshBrowse(), refreshMine(), refreshInventory()]);
      // Items changed hands. Nothing in the HUNT restarted - the server
      // reconfigured both running sessions in place.
      onSettled();
    } catch (unknownError) {
      report(unknownError);
      await refreshBrowse();
    } finally {
      setBusy(false);
    }
  };

  const owned = inventory?.instances ?? [];
  /**
   * The counts on the sell tab's drawers.
   *
   * Only there: the vault is fully loaded so the numbers are real, while the
   * public browse is paginated and any count this screen invented for it would
   * be a guess about somebody else's page.
   */
  const sellCounts = countByMarketCategory(owned.map((item) => item.definitionId));
  /** Drawer first, then the typed name - the search works INSIDE the drawer. */
  const visible = owned.filter(
    (item) =>
      isInMarketCategory(item.definitionId, category) &&
      (sellSearch.trim() === '' ||
        itemSearchKey(itemDisplayName(item.definitionId)).includes(
          itemSearchKey(sellSearch.trim()),
        )),
  );
  const eligible = visible.filter((item) => item.availableForTrade);
  const ineligible = visible.filter((item) => !item.availableForTrade);
  /**
   * The item the seller picked, wherever it is.
   *
   * Looked up in the WHOLE vault rather than in the filtered list: changing
   * drawer must not silently unpick what somebody already chose, and the
   * preview below has to keep showing it so they can still see - and remove -
   * their own selection.
   */
  const picked = owned.find((item) => item.itemId === sellItem) ?? null;

  /** Clears the draft completely. A half-cleared form is how an accident happens. */
  const clearSelection = () => {
    setSellItem(null);
    setSellPrice('0');
  };

  return (
    <aside className="market-panel" data-testid="market-panel" aria-label="Mercado de itens">
      <header className="market-panel-header">
        <h2>Mercado</h2>
        <button
          type="button"
          className="overview-close"
          data-testid="market-close"
          aria-label="Fechar mercado"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {error ? (
        <p className="trade-error" role="alert" data-testid="market-error">
          {error === 'insufficient_funds'
            ? 'Saldo insuficiente.'
            : error === 'own_listing'
              ? 'Você não pode comprar o próprio anúncio.'
              : error === 'listing_not_active'
                ? 'Este anúncio não está mais disponível.'
                : error === 'price_changed'
                  ? 'O anúncio mudou. Confira o preço atualizado.'
                  : error}
        </p>
      ) : null}

      <div className="trade-tabs" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={tab === entry.key}
            className={tab === entry.key ? 'is-active' : ''}
            data-testid={`market-tab-${entry.key}`}
            onClick={() => setTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'buy' ? (
        <section className="market-body" data-testid="market-buy">
          <CategoryTabs value={category} disabled={busy} onChange={setCategory} />
          <div className="market-filters">
            <input
              type="text"
              placeholder="Nome do item"
              value={nameFilter}
              data-testid="market-filter-name"
              onChange={(event) => setNameFilter(event.target.value)}
            />
            <select
              value={currencyFilter}
              data-testid="market-filter-currency"
              onChange={(event) => setCurrencyFilter(event.target.value as '' | 'gold' | 'cash')}
            >
              <option value="">Gold e Cash</option>
              <option value="gold">Somente Gold</option>
              <option value="cash">Somente Cash</option>
            </select>
            <select
              value={sort}
              data-testid="market-sort"
              onChange={(event) => setSort(event.target.value as MarketSort)}
            >
              {SORTS.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
            {/* Manual refresh, not a three-second poll of the whole public
                list: a browse is somebody else's page and re-fetching it
                constantly is a request nobody asked for. */}
            <button
              type="button"
              className="ghost-button"
              data-testid="market-refresh"
              disabled={busy}
              onClick={() => void refreshBrowse()}
            >
              Atualizar
            </button>
          </div>

          <ul className="market-grid" data-testid="market-listings">
            {(browse?.listings ?? []).length === 0 ? (
              <li className="trade-empty">Nenhum anúncio.</li>
            ) : null}
            {(browse?.listings ?? []).map((listing) => (
              <li key={listing.listingId} style={rarityStyle(listing.item.rarity)}>
                <ListingCard
                  listing={listing}
                  manifest={manifest}
                  disabled={busy}
                  onBuy={() => setConfirming(listing)}
                />
              </li>
            ))}
          </ul>
          <LoadMore
            cursor={browse?.nextCursor ?? null}
            disabled={busy}
            onLoad={() => void loadMore('buy')}
            testId="market-more-buy"
          />
        </section>
      ) : null}

      {tab === 'sell' ? (
        <section className="market-body market-sell" data-testid="market-sell">
          {/* The fee is resolved at SETTLEMENT, and a seller has to know that
              before they price anything. */}
          <p className="trade-note" data-testid="market-fee-notice">
            {MARKET_FEE_TIMING_NOTICE}
          </p>
          <p
            className={quote?.feeTier === 'vip' ? 'market-fee-vip' : 'market-fee-standard'}
            data-testid="market-fee-tier"
          >
            {quote?.feeTier === 'vip'
              ? `TAXA VIP DE ${MARKET_FEE_VIP_PERCENT}%`
              : `TAXA DE ${MARKET_FEE_NON_VIP_PERCENT}%`}
          </p>

          <CategoryTabs
            value={category}
            counts={sellCounts}
            disabled={busy}
            onChange={setCategory}
          />
          {/* Searches INSIDE the open drawer, so a name and a category narrow
              together instead of one replacing the other. */}
          <div className="market-filters">
            <input
              type="text"
              placeholder="Nome do item"
              value={sellSearch}
              data-testid="market-sell-search"
              onChange={(event) => setSellSearch(event.target.value)}
            />
          </div>

          {/* The list scrolls; the preview, the price form and ANUNCIAR below
              it do not, so the action a seller came here for is never scrolled
              off. */}
          <div className="market-sell-scroll">
            <h3>Item</h3>
            <ul className="market-rows" data-testid="market-sell-items">
              {eligible.length === 0 ? (
                <li className="trade-empty">Nenhum item elegível.</li>
              ) : null}
              {eligible.map((item) => (
                <li
                  key={item.itemId}
                  className={sellItem === item.itemId ? 'is-picked' : ''}
                  data-testid={`market-sell-row-${item.itemId}`}
                >
                  <ItemThumb manifest={manifest} item={item} size="row" />
                  <span className="market-row-main">
                    <span className="market-row-name">{itemDisplayName(item.definitionId)}</span>
                    <span className="market-row-sub">
                      {rarityLabel(item.rarity)} · {itemLevelLabel(item.definitionId, item.level)} ·
                      ×1
                    </span>
                  </span>
                  <span className="market-state">
                    {item.equipped ? 'EQUIPADO' : item.activePet ? 'PET ATIVO' : 'DISPONÍVEL'}
                  </span>
                  {/* Red when it removes, and only then. Picking and unpicking
                      are opposite actions and must not share one look. */}
                  <button
                    type="button"
                    className={sellItem === item.itemId ? 'danger-button' : 'ghost-button'}
                    data-testid={`market-pick-${item.itemId}`}
                    disabled={busy}
                    onClick={() =>
                      sellItem === item.itemId ? clearSelection() : setSellItem(item.itemId)
                    }
                  >
                    {sellItem === item.itemId ? 'REMOVER' : 'ESCOLHER'}
                  </button>
                </li>
              ))}
            </ul>

            {/* The rest, behind a toggle, each saying WHY. Hiding them entirely
                would leave a player hunting for an item they can see everywhere
                else in the game - which is also why they keep their picture and
                their rarity border, only dimmed. An item with no thumbnail
                reads as a broken asset, not as an unavailable item. */}
            {ineligible.length > 0 ? (
              <details className="trade-ineligible" data-testid="market-ineligible">
                <summary>Indisponíveis ({ineligible.length})</summary>
                <ul className="market-rows">
                  {ineligible.map((item) => (
                    <li
                      key={item.itemId}
                      className="is-blocked"
                      data-testid={`market-blocked-row-${item.itemId}`}
                    >
                      <ItemThumb manifest={manifest} item={item} size="row" />
                      <span className="market-row-main">
                        <span className="market-row-name">
                          {itemDisplayName(item.definitionId)}
                        </span>
                        <span className="market-row-sub">
                          {rarityLabel(item.rarity)} ·{' '}
                          {itemLevelLabel(item.definitionId, item.level)} · ×1
                        </span>
                      </span>
                      <span className="trade-block-reason">
                        {TRADE_BLOCK_MESSAGES[item.unavailableReason ?? 'unavailable']}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>

          <div className="market-sell-actions">
            {/*
              What is about to be listed, with its own picture.
              Beside the price rather than above the list, because the two
              numbers a seller is deciding - the price and what they keep -
              belong next to the thing being sold. It survives typing a price,
              switching Gold/Cash and CHANGING DRAWER: the item is looked up in
              the whole vault, so a seller who wandered off to another category
              can still see what they picked and still take it back.
            */}
            {picked ? (
              <div
                className="market-preview"
                data-testid="market-preview"
                style={rarityStyle(picked.rarity)}
              >
                <ItemThumb manifest={manifest} item={picked} size="preview" />
                <div className="market-preview-body">
                  <span className="market-row-name" data-testid="market-preview-name">
                    {itemDisplayName(picked.definitionId)}
                  </span>
                  <span className="market-row-sub">
                    {rarityLabel(picked.rarity)} ·{' '}
                    {itemLevelLabel(picked.definitionId, picked.level)} · ×1 ·{' '}
                    {MARKET_CATEGORY_LABELS[marketCategoryOf(picked.definitionId)]}
                  </span>
                  <span className="market-preview-money">
                    <span className="market-card-price">
                      {formatDecimal(sellPrice)} {sellCurrency === 'gold' ? 'Gold' : 'Cash'}
                    </span>
                    <span className="market-estimate">
                      taxa ~{formatDecimal(quote?.estimatedFee ?? '0')} · líquido ~
                      {formatDecimal(quote?.estimatedNet ?? '0')}
                    </span>
                  </span>
                </div>
                <span className="market-preview-state">SELECIONADO</span>
                <button
                  type="button"
                  className="danger-button"
                  data-testid="market-preview-remove"
                  disabled={busy}
                  onClick={clearSelection}
                >
                  REMOVER ITEM
                </button>
              </div>
            ) : (
              // Discreet, and the same height, so choosing an item does not make
              // the form jump under the cursor.
              <p className="market-preview is-empty" data-testid="market-preview-empty">
                Selecione um item para anunciar
              </p>
            )}

            {/* One currency or the other. There is no hybrid price. */}
            <div className="market-sell-form">
              <label>
                Moeda
                <select
                  value={sellCurrency}
                  data-testid="market-sell-currency"
                  onChange={(event) => setSellCurrency(event.target.value as 'gold' | 'cash')}
                >
                  <option value="gold">Gold</option>
                  <option value="cash">Cash</option>
                </select>
              </label>
              <label>
                Preço
                <input
                  type="text"
                  inputMode="numeric"
                  value={sellPrice}
                  data-testid="market-sell-price"
                  onChange={(event) => setSellPrice(digitsOnly(event.target.value))}
                />
              </label>
            </div>

            {/* One line, not two. The preview above says the same thing about
                the picked item; this is what answers a seller who typed a
                price before choosing anything, and two stacked paragraphs were
                pushing ANUNCIAR down a 768px screen for no information. */}
            <p className="market-quote-line">
              Taxa estimada:{' '}
              <strong data-testid="market-quote-fee">
                {formatDecimal(quote?.estimatedFee ?? '0')}
              </strong>{' '}
              · Você receberá:{' '}
              <strong data-testid="market-quote-net">
                {formatDecimal(quote?.estimatedNet ?? '0')}
              </strong>
            </p>

            <button
              type="button"
              className="neon-button"
              data-testid="market-list"
              disabled={busy || !sellItem || sellPrice === '0'}
              onClick={() =>
                void run(async () => {
                  if (!sellItem) {
                    return;
                  }
                  await createMarketListing({
                    instanceId: sellItem,
                    currency: sellCurrency,
                    price: sellPrice,
                  });
                  clearSelection();
                  setTab('mine');
                })
              }
            >
              ANUNCIAR
            </button>
          </div>
        </section>
      ) : null}

      {tab === 'mine' ? (
        <section className="market-body" data-testid="market-mine">
          <CategoryTabs value={category} disabled={busy} onChange={setCategory} />
          <ul className="market-rows">
            {(mine?.listings ?? []).length === 0 ? (
              <li className="trade-empty">Nenhum anúncio.</li>
            ) : null}
            {(mine?.listings ?? []).map((listing) => (
              <li key={listing.listingId} data-testid={`market-mine-row-${listing.listingId}`}>
                {/* A sold, cancelled or expired listing keeps its picture. The
                    row describes the ITEM that was on sale, and the item did
                    not stop being that item when the listing ended. */}
                <ItemThumb manifest={manifest} item={listing.item} size="row" />
                <span className="market-row-main">
                  <span className="market-row-name">{listing.item.name}</span>
                  <span className="market-row-sub">
                    {rarityLabel(listing.item.rarity)} · {listing.item.levelLabel} · ×
                    {formatDecimal(listing.item.quantity)}
                  </span>
                  {/*
                   * O mesmo facto, na linha do próprio vendedor.
                   *
                   * Ele tem de o ver antes de decidir o preço: o que está à
                   * venda inclui os corações, e eles vão embora com a nave. Um
                   * anúncio TERMINADO não a mostra - `null` - porque a nave já
                   * é de outra pessoa e o que ela tem agora não é assunto de
                   * quem a vendeu.
                   */}
                  {listing.item.primordialHeartCount === null ? null : (
                    <span
                      className="market-row-sub"
                      data-testid={`market-mine-hearts-${listing.listingId}`}
                    >
                      {listing.item.primordialHeartCount} Corações Primordiais · acompanham a nave
                    </span>
                  )}
                </span>
                <span className="market-row-money">
                  <span className="market-card-price">
                    {formatDecimal(listing.price)} {listing.currency === 'gold' ? 'Gold' : 'Cash'}
                  </span>
                  {/* An ESTIMATE at today's rate. The real fee is decided when
                      it sells, which is what the sell tab's notice says. */}
                  {listing.state === 'active' ? (
                    <span className="market-estimate">
                      taxa ~
                      {formatDecimal(estimateFee(listing.price, quote?.feeTier ?? 'standard'))} ·
                      líquido ~
                      {formatDecimal(estimateNet(listing.price, quote?.feeTier ?? 'standard'))}
                    </span>
                  ) : null}
                </span>
                <span className="market-state">{STATE_LABELS[listing.state] ?? listing.state}</span>
                <span className="market-when">{remainingLabel(listing.expiresAt)}</span>
                {listing.state === 'active' ? (
                  <button
                    type="button"
                    className="ghost-button"
                    data-testid={`market-cancel-${listing.listingId}`}
                    disabled={busy}
                    onClick={() => void run(() => cancelMarketListing(listing.listingId))}
                  >
                    CANCELAR
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <LoadMore
            cursor={mine?.nextCursor ?? null}
            disabled={busy}
            onLoad={() => void loadMore('mine')}
            testId="market-more-mine"
          />
        </section>
      ) : null}

      {tab === 'history' ? (
        <section className="market-body" data-testid="market-history">
          <CategoryTabs value={category} disabled={busy} onChange={setCategory} />
          <ul className="market-rows">
            {(history?.entries ?? []).length === 0 ? (
              <li className="trade-empty">Nada ainda.</li>
            ) : null}
            {(history?.entries ?? []).map((entry) => (
              <li
                key={entry.listingId}
                className={`outcome-${entry.outcome}`}
                data-testid={`market-history-row-${entry.listingId}`}
              >
                {/* The picture survives the sale. The history row is built from
                    the LISTING's own snapshot - definition, level, rarity - so
                    it does not depend on the item still belonging to anybody in
                    particular, or on it still existing in this account's vault. */}
                <ItemThumb manifest={manifest} item={entry.item} size="row" />
                <span className="market-row-main">
                  <span className="market-row-name">{entry.item.name}</span>
                  <span className="market-row-sub">
                    {rarityLabel(entry.item.rarity)} · {entry.item.levelLabel} · ×
                    {formatDecimal(entry.item.quantity)}
                  </span>
                </span>
                <span className="market-row-money">
                  <span className="market-card-price">
                    {formatDecimal(entry.price)} {entry.currency === 'gold' ? 'Gold' : 'Cash'}
                  </span>
                  {/* The fee is the SELLER's business; a buyer sees the price. A
                      cancellation and an expiry carry none, because none was
                      charged. */}
                  {entry.outcome === 'sold' && entry.role === 'seller' && entry.net ? (
                    <span className="market-estimate">
                      recebeu {formatDecimal(entry.net)} · taxa {formatDecimal(entry.fee ?? '0')}
                    </span>
                  ) : null}
                </span>
                {/* Four different things live in this list, so each says which
                    it is rather than leaving a seller to infer it. */}
                <span className="market-outcome">{outcomeLabel(entry)}</span>
                <span className="market-when">
                  {entry.counterpartyName ? `${entry.counterpartyName} · ` : ''}
                  {new Date(entry.endedAt).toLocaleString('pt-BR')}
                </span>
              </li>
            ))}
          </ul>
          <LoadMore
            cursor={history?.nextCursor ?? null}
            disabled={busy}
            onLoad={() => void loadMore('history')}
            testId="market-more-history"
          />
        </section>
      ) : null}

      {/* Confirmation before any purchase, showing the EXACT price. */}
      {confirming ? (
        <div className="market-confirm" data-testid="market-confirm" role="dialog">
          <p>
            Comprar <strong>{confirming.item.name}</strong> por{' '}
            <strong>
              {formatDecimal(confirming.price)} {confirming.currency === 'gold' ? 'Gold' : 'Cash'}
            </strong>
            ?
          </p>
          <div className="market-confirm-actions">
            <button
              type="button"
              className="ghost-button"
              data-testid="market-confirm-cancel"
              onClick={() => setConfirming(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="neon-button"
              data-testid="market-confirm-buy"
              disabled={busy}
              onClick={() => {
                const listing = confirming;
                setConfirming(null);
                void run(() =>
                  buyMarketListing({
                    listingId: listing.listingId,
                    // The price this screen showed. The server charges the
                    // LISTING's price; this only makes a stale screen a
                    // refusal rather than a silent charge.
                    expectedPrice: listing.price,
                    expectedCurrency: listing.currency,
                  }),
                );
              }}
            >
              CONFIRMAR COMPRA
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

/**
 * The next-page control, or an honest end-of-list.
 *
 * Always visible rather than hidden behind a scroll position: a player should
 * not have to discover that more exists.
 */
function LoadMore({
  cursor,
  disabled,
  onLoad,
  testId,
}: {
  cursor: string | null;
  disabled: boolean;
  onLoad: () => void;
  testId: string;
}) {
  if (!cursor) {
    return null;
  }
  return (
    <button
      type="button"
      className="ghost-button market-more"
      data-testid={testId}
      disabled={disabled}
      onClick={onLoad}
    >
      Carregar mais
    </button>
  );
}

/**
 * One card in the buy grid.
 *
 * The art takes practically the whole tile, and the rarity border and animation
 * are the ones the inventory already uses - a marketplace card and an inventory
 * card must read as the same item.
 */
function ListingCard({
  listing,
  manifest,
  disabled,
  onBuy,
}: {
  listing: MarketListingWire;
  manifest: AssetManifest;
  disabled: boolean;
  onBuy: () => void;
}) {
  return (
    <article className="market-card" data-rarity={listing.item.rarity}>
      <ItemThumb manifest={manifest} item={listing.item} size="card" />
      <div className="market-card-body">
        <span className="market-card-name">{listing.item.name}</span>
        <span className="market-card-sub">
          {rarityLabel(listing.item.rarity)} · {listing.item.levelLabel}
          {listing.item.kind === 'stack' ? ` · ×${formatDecimal(listing.item.quantity)}` : ''}
        </span>
        {/*
         * Os Corações Primordiais que acompanham esta nave (gate V28F.B).
         *
         * ## Porque a frase diz "acompanham"
         *
         * Porque é a única coisa que um comprador precisa de saber e que nada
         * mais no cartão diz: a contagem não fica com o vendedor. Ela está
         * incorporada NA NAVE, viaja com ela, e o comprador recebe-a inteira.
         * Um número sozinho deixaria a pergunta em aberto, e a pergunta é a que
         * decide o preço.
         *
         * ## Porque só aparece quando o anúncio a tem
         *
         * `null` significa duas coisas, e nenhuma delas é zero: a categoria não
         * tem corações (um artefacto não tem), ou o anúncio terminou e a
         * contagem é uma propriedade viva de uma nave que já é de outra pessoa.
         * Nos dois casos a linha não existe, em vez de existir a mentir.
         *
         * Uma nave anunciada COM zero mostra `0`, que é um facto que ajuda a
         * decidir tanto quanto um número grande.
         */}
        {listing.item.primordialHeartCount === null ? null : (
          <span className="market-card-hearts" data-testid={`market-hearts-${listing.listingId}`}>
            {listing.item.primordialHeartCount} Corações Primordiais · acompanham a nave
          </span>
        )}
        <span className="market-card-price">
          {formatDecimal(listing.price)} {listing.currency === 'gold' ? 'Gold' : 'Cash'}
        </span>
        <span className="market-card-seller">{listing.sellerName}</span>
        <button
          type="button"
          className="neon-button"
          data-testid={`market-buy-${listing.listingId}`}
          // Your own listing is refused by the server anyway; hiding the button
          // saves a round trip and a confusing error.
          disabled={disabled || listing.isMine}
          onClick={onBuy}
        >
          {listing.isMine ? 'SEU ANÚNCIO' : 'COMPRAR'}
        </button>
      </div>
    </article>
  );
}
